import React, { useState } from 'react';
import { ChevronsDown, ChevronsUp, Link2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { setAllCollapsibleSections } from './CollapsibleSection';
import { cn } from '../../lib/utils';

/**
 * PageToolbar — one consolidated, theme-aware control cluster for the
 * page-hero `actions` slot. Replaces the separate white-pill
 * ExpandCollapseAllControl + CopyShareLinkButton, which rendered as
 * white-on-dark on the dark canvases.
 *
 * Renders a single segmented group: [Expand all · Collapse all] | [Share].
 * Sections are optional — pass `collapsePrefix` to show Expand/Collapse,
 * pass `share` (default true) to show the Share button.
 *
 * Light/dark is derived from the page `theme` (matches PageHero's
 * detection): a `textBase` containing "slate" or a slate-50 hero
 * gradient == light hero.
 *
 * Props:
 *   theme            — the section theme object passed to PageHero.
 *   collapsePrefix   — storageKey namespace for expand/collapse-all.
 *                      Omit to hide those buttons.
 *   share            — show the Share button (default true).
 *   organizationId   — baked into the shared URL as ?organization_id=.
 *   testId           — root data-testid (default 'page-toolbar').
 */
export default function PageToolbar({
  theme,
  collapsePrefix,
  share = true,
  organizationId,
  testId = 'page-toolbar',
}) {
  const [copied, setCopied] = useState(false);
  const isLight = !theme
    || String(theme?.textBase || '').includes('slate')
    || theme?.heroGradient === 'bg-slate-50';

  const showCollapse = !!collapsePrefix;

  const group = isLight
    ? 'bg-white border-slate-200'
    : 'bg-[#111827] border-[#1f2a44]';
  const btn = isLight
    ? 'text-slate-700 hover:bg-slate-100'
    : 'text-slate-300 hover:bg-white/5 hover:text-white';
  const divider = isLight ? 'bg-slate-200' : 'bg-[#1f2a44]';

  const handleCopy = async () => {
    const url = new URL(window.location.href);
    if (organizationId) url.searchParams.set('organization_id', organizationId);
    else url.searchParams.delete('organization_id');
    try {
      await navigator.clipboard.writeText(url.toString());
      setCopied(true);
      toast.success('Shareable link copied — opens this org view for the recipient');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy link to clipboard');
    }
  };

  if (!showCollapse && !share) return null;

  return (
    <div
      className={cn('inline-flex items-center rounded-lg border shadow-sm overflow-hidden', group)}
      data-testid={testId}
    >
      {showCollapse && (
        <>
          <button
            type="button"
            onClick={() => setAllCollapsibleSections(collapsePrefix, true)}
            className={cn('inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors', btn)}
            data-testid={`${testId}-expand`}
            title="Expand every section on this page"
          >
            <ChevronsDown size={13} /> Expand
          </button>
          <span className={cn('w-px self-stretch my-1.5', divider)} aria-hidden="true" />
          <button
            type="button"
            onClick={() => setAllCollapsibleSections(collapsePrefix, false)}
            className={cn('inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors', btn)}
            data-testid={`${testId}-collapse`}
            title="Collapse every section on this page"
          >
            <ChevronsUp size={13} /> Collapse
          </button>
        </>
      )}
      {showCollapse && share && <span className={cn('w-px self-stretch my-1.5', divider)} aria-hidden="true" />}
      {share && (
        <button
          type="button"
          onClick={handleCopy}
          className={cn('inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition-colors', btn)}
          data-testid={`${testId}-share`}
          aria-label="Copy a shareable link to this view"
          title="Copy a shareable link that opens this org's view"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Link2 className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Share'}
        </button>
      )}
    </div>
  );
}
